import { memo } from 'react';
import styled, { css } from 'styled-components';
import { spacingNumeric, colors, sizeNumeric, typography, spacing } from '@izettle/otto';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { SemanticState } from '../../enumerations/semantic-state.js';
import { InputSize } from '../../enumerations/input-size.js';

const mixinFloatingLabelTextIcon = css`
  transform: translateX(${spacingNumeric.xxSmall * 2 + spacingNumeric.medium}px);
`;
const mixinFloatingLabelPalette = css`
  color: ${_ref => {
  let {
    semanticState,
    isErrorStateOverriden,
    isDisabled,
    isFocused,
    isElevated
  } = _ref;
  if (isErrorStateOverriden) {
    return isElevated ? colors.icon.error.large : colors.icon.error.small;
  }
  if (isDisabled) {
    return colors.text.disabled;
  }
  if (isFocused) {
    return colors.action.secondary.text.default;
  }
  return {
    [SemanticState.Warning]: isElevated ? colors.icon.warning.large : colors.input.text.warning,
    [SemanticState.Error]: isElevated ? colors.text.error : colors.icon.error.small,
    [SemanticState.Success]: isElevated ? colors.text.success : colors.text.success,
    [SemanticState.Default]: colors.text.secondary
  }[semanticState ?? SemanticState.Default];
}};
`;
const mixinFloatingLabelTextElevation = css`
  transform: translateY(
      -${_ref2 => {
  let {
    isElevated,
    size
  } = _ref2;
  if (!isElevated) {
    return;
  }
  return {
    [InputSize.Small]: sizeNumeric.small / 2,
    [InputSize.Medium]: sizeNumeric.xMedium / 2,
    [InputSize.Large]: sizeNumeric.large / 2
  }[size ?? InputSize.Large];
}}px
    )
    scale(0.75);
`;
const FloatingLabelText = /*#__PURE__*/memo(styled.label.withConfig({
  displayName: "floating-label__FloatingLabelText",
  componentId: "zettle__sc-npcg0p-0"
})(["", " box-sizing:border-box;pointer-events:none;position:absolute;margin-left:", ";margin-bottom:0;align-self:center;padding:0 ", ";transition:transform 0.1s,color 0.1s;transform-origin:0% 50%;z-index:1;width:100%;max-width:100%;text-overflow:ellipsis;white-space:nowrap;overflow:hidden;transition-timing-function:cubic-bezier(0.25,0.1,0.25,1);", " ", " ", " ", ""], typography.regular, spacing.xxSmall, spacing.xxSmall, _ref3 => {
  let {
    isWithIcon
  } = _ref3;
  return isWithIcon && mixinFloatingLabelTextIcon;
}, mixinFloatingLabelTextElevation, mixinFloatingLabelPalette, _ref4 => {
  let {
    isHidden
  } = _ref4;
  return isHidden && `opacity: 0;`;
}));
const FloatingLabelBackground = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "floating-label__FloatingLabelBackground",
  componentId: "zettle__sc-npcg0p-1"
})(["", " pointer-events:none;background-color:", ";color:transparent;position:absolute;padding:0 ", ";top:-2px;height:3px;margin-left:", ";transform-origin:0% 50%;transition:transform 0.1s,opacity 0.15s;transform:scaleX(0);opacity:0;max-width:100%;text-overflow:ellipsis;white-space:nowrap;overflow:hidden;transition-timing-function:cubic-bezier(0.25,0.1,0.25,1);", ""], typography.regular, colors.background.default, spacing.xxSmall, spacing.small, _ref5 => {
  let {
    isElevated,
    isHidden
  } = _ref5;
  return isElevated && !isHidden && css`
      transform: scaleX(0.75) translateX(-10px);
      opacity: 1;
    `;
}));
const FloatingLabel = _ref6 => {
  let {
    id,
    children,
    isHidden,
    isElevated,
    isFocused,
    isDisabled,
    isWithIcon,
    size,
    htmlFor,
    semanticState,
    isErrorStateOverriden
  } = _ref6;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(FloatingLabelText, {
      id: id,
      isHidden: isHidden,
      isElevated: isElevated,
      isFocused: isFocused,
      isWithIcon: isWithIcon,
      isDisabled: isDisabled,
      size: size,
      htmlFor: htmlFor,
      semanticState: semanticState,
      isErrorStateOverriden: isErrorStateOverriden,
      children: children
    }), /*#__PURE__*/jsx(FloatingLabelBackground, {
      isHidden: isHidden,
      isElevated: isElevated,
      "aria-hidden": true,
      children: children
    })]
  });
};

export { FloatingLabel };
//# sourceMappingURL=index.js.map
